import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { Plus, Trash2, ChevronDown, Edit2, Check, X } from 'lucide-react';
import CircularProgressBar from '../common/circular-progress';

const SubSectionHeader = ({ label, count, expanded, onToggle }) => (
  <div
    className="d-flex align-items-center p-2 hover-light pointer"
    onClick={onToggle}
    role="button"
    onKeyDown={e => e.key === 'Enter' && onToggle()}
  >
    <ChevronDown
      size={16}
      className="text-muted mr-2"
      style={{
        transition: 'transform 0.2s ease',
        transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)'
      }}
    />
    <span className="xsmallFont text-muted" style={{ fontWeight: 600 }}>
      {label} ({count})
    </span>
  </div>
);

const LocationSubSection = ({
  label,
  items,
  county,
  loading,
  icon: Icon,
  datalistOptions,
  onAdd,      // async (value) => { error }
  onSave,     // async (item, newValue) => { error }
  onDelete,   // (id, name) => void
  className = '',
}) => {
  const [expanded, setExpanded] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [editingLoading, setEditingLoading] = useState(false);

  const singularLabel = label === 'Cities' ? 'city' : 'municipality';
  const datalistId = `${label.toLowerCase()}-datalist-${county}`;

  const handleAdd = async () => {
    const value = inputValue.trim();
    if (!value) return;
    setIsAdding(true);
    const { error } = await onAdd(value);
    setIsAdding(false);
    if (!error) setInputValue('');
  };

  const handleStartEdit = item => {
    setEditingItem(item);
    setEditingValue(item.city);
  };

  const handleSaveEdit = async () => {
    const newValue = editingValue.trim();
    if (!editingItem || !newValue || newValue === editingItem.city) {
      setEditingItem(null);
      return;
    }
    setEditingLoading(true);
    await onSave(editingItem, newValue);
    setEditingLoading(false);
    setEditingItem(null);
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditingValue('');
  };

  return (
    <div className={className}>
      <SubSectionHeader label={label} count={items.length} expanded={expanded} onToggle={() => setExpanded(e => !e)} />
      {expanded && (
        <>
          <div style={{ marginBottom: '8px' }}>
            {items.length > 0 ? (
              items.map(item => (
                <div
                  key={item.id}
                  className="d-flex align-items-center justify-content-between p-2 rounded hover-light"
                  onMouseEnter={() => setHoveredItem(item.id)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      flex: 1,
                      minWidth: 0
                    }}
                  >
                    <Icon size={14} style={{ color: '#94a3b8', flexShrink: 0 }} />
                    {editingItem?.id === item.id ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          flex: 1,
                          minWidth: 0
                        }}
                      >
                        <input
                          type="text"
                          className="county-input-custom"
                          style={{ flex: 1, minWidth: 0, height: '34px', fontSize: '13px' }}
                          value={editingValue}
                          onChange={e => setEditingValue(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleSaveEdit();
                            if (e.key === 'Escape') handleCancelEdit();
                          }}
                          autoFocus
                          disabled={editingLoading}
                        />
                        <button
                          type="button"
                          className="county-edit-btn"
                          onClick={handleSaveEdit}
                          disabled={editingLoading || !editingValue?.trim()}
                          style={{ width: '28px', height: '28px', color: '#16a34a' }}
                          aria-label="Save"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          type="button"
                          className="county-edit-btn"
                          onClick={handleCancelEdit}
                          disabled={editingLoading}
                          style={{ width: '28px', height: '28px' }}
                          aria-label="Cancel"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <span style={{ fontSize: '14px', fontWeight: 500, color: '#1e293b' }}>{item.city}</span>
                    )}
                  </div>
                  {editingItem?.id !== item.id && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <button
                        type="button"
                        className="county-edit-btn"
                        onClick={() => handleStartEdit(item)}
                        style={{
                          width: '28px',
                          height: '28px',
                          opacity: hoveredItem === item.id ? 1 : 0
                        }}
                        aria-label={`Edit ${singularLabel}`}
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        type="button"
                        className="county-delete-btn"
                        onClick={() => onDelete(item.id, item.city)}
                        style={{
                          width: '28px',
                          height: '28px',
                          opacity: hoveredItem === item.id ? 1 : 0
                        }}
                        aria-label={`Delete ${singularLabel}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p style={{ margin: '8px 0', fontSize: '13px', color: '#94a3b8', textAlign: 'center' }}>
                No {label.toLowerCase()} added yet
              </p>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              gap: '12px',
              paddingTop: '8px',
              borderTop: '1px solid #f1f5f9',
              marginBottom: label === 'Municipalities' ? '10px' : '0'
            }}
          >
            <input
              type="text"
              className="county-input-custom"
              style={{ flex: 1, height: '38px', fontSize: '13px' }}
              placeholder={`Add ${singularLabel}...`}
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && inputValue.trim() && handleAdd()}
              disabled={loading}
              list={datalistOptions ? datalistId : undefined}
            />
            {datalistOptions && (
              <datalist id={datalistId}>
                {datalistOptions.map(name => (
                  <option key={name} value={name} />
                ))}
              </datalist>
            )}
            <Button
              size="sm"
              variant="success"
              onClick={handleAdd}
              disabled={isAdding || !inputValue.trim()}
              className="d-flex align-items-center justify-content-center"
            >
              {isAdding ? (
                <div className="d-inline-flex align-items-center justify-content-center mr-2">
                  <CircularProgressBar size={1.5} light />
                </div>
              ) : (
                <Plus size={14} className="mr-1" />
              )}
              {isAdding ? 'Adding...' : 'Add'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default LocationSubSection;
